import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import { AlertTriangle, TrendingDown, Activity, Zap, ChevronDown, ChevronRight, Users } from "lucide-react";
import { useAlertsSummary, type AlertAnimal } from "@/hooks/use-behavior-analytics";

const TREND_COLORS = {
  worsening: "hsl(0, 84%, 60%)",
  stable: "hsl(215, 20%, 65%)",
  improving: "hsl(142, 71%, 45%)",
  insufficient: "hsl(220, 13%, 85%)",
};

function AlertStatCard({
  icon: Icon,
  label,
  value,
  total,
  color,
  isLoading,
  testId,
}: {
  icon: React.ElementType;
  label: string;
  value: number;
  total: number;
  color: string;
  isLoading: boolean;
  testId: string;
}) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{label}</CardTitle>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-8 w-16" />
        ) : (
          <>
            <div className="text-2xl font-bold" data-testid={testId} style={{ color }}>
              {value}
            </div>
            <p className="text-xs text-muted-foreground">
              {pct}% of {total} tracked
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function AnimalList({ animals, showDetail }: { animals: AlertAnimal[]; showDetail: "streak" | "spike" | "severity" }) {
  if (animals.length === 0) {
    return <p className="text-sm text-muted-foreground py-2">None flagged</p>;
  }
  return (
    <div className="divide-y">
      {animals.slice(0, 10).map((a) => (
        <div key={a.animalId} className="flex items-center justify-between py-2 gap-3" data-testid={`row-alert-animal-${a.animalId}`}>
          <div className="min-w-0">
            <p className="text-sm font-medium truncate">{a.name}</p>
            <p className="text-xs text-muted-foreground">{a.species} · {a.location}</p>
          </div>
          <div className="shrink-0">
            {showDetail === "streak" && a.streakDays != null && (
              <Badge variant="outline" className="text-xs">{a.streakDays}d streak</Badge>
            )}
            {showDetail === "spike" && a.spikeRate != null && (
              <Badge variant="outline" className="text-xs">{a.spikeRate.toFixed(1)}x rate</Badge>
            )}
            {showDetail === "severity" && a.severityCount != null && (
              <Badge variant="outline" className="text-xs">{a.severityCount} high</Badge>
            )}
          </div>
        </div>
      ))}
      {animals.length > 10 && (
        <p className="text-xs text-muted-foreground pt-2">+{animals.length - 10} more</p>
      )}
    </div>
  );
}

function CollapsibleAlertGroup({
  title,
  icon: Icon,
  animals,
  showDetail,
  defaultOpen,
}: {
  title: string;
  icon: React.ElementType;
  animals: AlertAnimal[];
  showDetail: "streak" | "spike" | "severity";
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen ?? false);
  return (
    <div>
      <button
        type="button"
        className="flex w-full items-center gap-2 py-2 text-sm font-medium hover-elevate rounded-md px-1"
        onClick={() => setOpen((v) => !v)}
        data-testid={`button-collapse-${title.toLowerCase().replace(/\s+/g, "-")}`}
      >
        <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
        <span className="flex-1 text-left">{title}</span>
        <Badge variant="secondary" className="text-xs">{animals.length}</Badge>
        {open ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
      </button>
      {open && (
        <div className="mt-1 ml-6">
          <AnimalList animals={animals} showDetail={showDetail} />
        </div>
      )}
    </div>
  );
}

interface BehaviorAlertsSummaryProps {
  selectedLocation?: string;
  isSystemView: boolean;
  showHints?: boolean;
}

export function BehaviorAlertsSummary({ selectedLocation, isSystemView, showHints }: BehaviorAlertsSummaryProps) {
  const location = isSystemView ? undefined : selectedLocation;
  const { data, isLoading } = useAlertsSummary(location, "DOG");

  const pieData = data
    ? [
        { name: "Worsening", value: data.trendDistribution.worsening, fill: TREND_COLORS.worsening },
        { name: "Stable", value: data.trendDistribution.stable, fill: TREND_COLORS.stable },
        { name: "Improving", value: data.trendDistribution.improving, fill: TREND_COLORS.improving },
        { name: "Insufficient data", value: data.trendDistribution.insufficient, fill: TREND_COLORS.insufficient },
      ].filter((d) => d.value > 0)
    : [];

  return (
    <div className="space-y-4">
      {showHints && (
        <div className="p-2 rounded-md bg-primary/10 border border-primary/20 text-xs text-primary">
          Behavior signals are automatically detected from ingested interaction data — surfacing dogs with patterns like worsening trends, multi-day streaks, or spikes in high-severity interactions. Dogs only — cats are excluded from signal metrics.
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Tracked Dogs</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <>
                <div className="text-2xl font-bold" data-testid="text-alert-total-tracked">{data?.totalTracked ?? 0}</div>
                <p className="text-xs text-muted-foreground">with alert metrics</p>
              </>
            )}
          </CardContent>
        </Card>

        <AlertStatCard
          icon={AlertTriangle}
          label="Struggling Now"
          value={data?.strugglingNow.count ?? 0}
          total={data?.totalTracked ?? 0}
          color="hsl(0, 84%, 60%)"
          isLoading={isLoading}
          testId="text-alert-struggling"
        />
        <AlertStatCard
          icon={Activity}
          label="Streak Flagged"
          value={data?.streakFlagged.count ?? 0}
          total={data?.totalTracked ?? 0}
          color="hsl(25, 95%, 53%)"
          isLoading={isLoading}
          testId="text-alert-streak"
        />
        <AlertStatCard
          icon={Zap}
          label="Daily Spike"
          value={data?.dailySpikeFlagged.count ?? 0}
          total={data?.totalTracked ?? 0}
          color="hsl(45, 93%, 47%)"
          isLoading={isLoading}
          testId="text-alert-spike"
        />
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium">3-Day Trend Distribution</CardTitle>
            <CardDescription>How tracked dogs are trending</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="h-48 flex items-center justify-center">
                <Skeleton className="h-36 w-36 rounded-full" />
              </div>
            ) : pieData.length === 0 ? (
              <div className="h-48 flex items-center justify-center text-sm text-muted-foreground">
                No trend data available
              </div>
            ) : (
              <div className="flex items-center gap-4">
                <ResponsiveContainer width="50%" height={160}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={44}
                      outerRadius={68}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {pieData.map((entry, i) => (
                        <Cell key={i} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value: number, name: string) => [value, name]}
                      contentStyle={{ fontSize: 12 }}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div className="space-y-2 flex-1">
                  {pieData.map((entry) => (
                    <div key={entry.name} className="flex items-center gap-2 text-xs">
                      <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: entry.fill }} />
                      <span className="flex-1 text-muted-foreground">{entry.name}</span>
                      <span className="font-medium" data-testid={`text-trend-${entry.name.toLowerCase().split(" ")[0]}`}>{entry.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium">Flagged Dogs</CardTitle>
            <CardDescription>Expand to see which dogs are flagged</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-3">
                {[0, 1, 2].map((i) => <Skeleton key={i} className="h-8 w-full" />)}
              </div>
            ) : (
              <div className="space-y-1" data-testid="panel-flagged-animals">
                <CollapsibleAlertGroup
                  title="Struggling Now"
                  icon={AlertTriangle}
                  animals={data?.strugglingNow.animals ?? []}
                  showDetail="severity"
                  defaultOpen={false}
                />
                <CollapsibleAlertGroup
                  title="Streak Flagged"
                  icon={Activity}
                  animals={data?.streakFlagged.animals ?? []}
                  showDetail="streak"
                />
                <CollapsibleAlertGroup
                  title="Daily Spike"
                  icon={Zap}
                  animals={data?.dailySpikeFlagged.animals ?? []}
                  showDetail="spike"
                />
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
